import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Lightbulb, RefreshCw, Settings, AlertTriangle, TrendingUp, 
  DollarSign, Zap, BarChart3, LineChart, ChevronDown, ChevronUp,
  ExternalLink, ArrowRight, Check, X, Clock, Users, Calendar,
  PieChart, Award, AlertCircle, Shuffle, User, LayoutGrid, BarChart2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { useFilters } from '../context/FilterContext';
import EmployeeDrillDown from '../components/EmployeeDrillDown';
import { useCachedData } from '../hooks/useCachedData';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_CONFIG = {
  critical_alert: {
    label: 'Critical Alerts',
    icon: AlertTriangle,
    color: 'bg-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    description: 'Issues requiring immediate attention'
  },
  trending: {
    label: 'Trending Issues',
    icon: TrendingUp,
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    description: 'Problems getting worse over time'
  },
  cost_driver: {
    label: 'Cost Drivers',
    icon: DollarSign,
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    description: 'Where the money is going'
  },
  quick_win: {
    label: 'Quick Wins',
    icon: Zap,
    color: 'bg-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    description: 'Easy fixes with high impact'
  },
  benchmark: {
    label: 'Benchmarks',
    icon: BarChart3,
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    description: 'Comparisons and performance metrics'
  },
  prediction: {
    label: 'Predictions',
    icon: LineChart,
    color: 'bg-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-700',
    description: 'What to expect based on trends'
  }
};

const ICON_MAP = {
  'alert-triangle': AlertTriangle,
  'trending-up': TrendingUp,
  'pie-chart': PieChart,
  'user': User,
  'users': Users,
  'shuffle': Shuffle,
  'calendar': Calendar,
  'award': Award,
  'alert-circle': AlertCircle
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const InsightCard = ({ insight, onViewDashboard, onViewRecommendation, onViewEmployee }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = CATEGORY_CONFIG[insight.category] || CATEGORY_CONFIG.cost_driver;
  const IconComponent = ICON_MAP[insight.icon] || config.icon;
  
  const urgencyColors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-green-100 text-green-700 border-green-200'
  };
  
  const easeColors = {
    easy: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    hard: 'bg-red-100 text-red-700'
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border ${config.borderColor} ${config.bgColor} overflow-hidden`}
      data-testid={`insight-card-${insight.id}`}
    >
      <div 
        className="p-4 cursor-pointer hover:bg-white/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-10 h-10 ${config.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <IconComponent className="text-white" size={20} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={urgencyColors[insight.urgency]}>
                {insight.urgency} urgency
              </Badge>
              {insight.ease && (
                <Badge variant="outline" className={easeColors[insight.ease]}>
                  {insight.ease} fix
                </Badge>
              )}
            </div>
            <h3 className={`font-semibold ${config.textColor} text-sm`}>
              {insight.title}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {insight.description}
            </p>
          </div>
          
          {/* Dollar Impact */}
          <div className="text-right flex-shrink-0">
            {insight.dollar_impact > 0 && (
              <div className="text-lg font-bold text-green-600">
                {formatCurrency(insight.dollar_impact)}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              {insight.dollar_impact > 0 ? 'impact' : ''}
            </div>
            <button className="mt-2">
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-200 bg-white"
          >
            <div className="p-4 space-y-4">
              {/* Period Comparison - show for insights with comparison data */}
              {insight.comparison && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="text-xs font-medium text-blue-700 mb-2">Period Comparison</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-blue-600">{insight.comparison.prior_period}</div>
                      <div className="font-semibold text-blue-800">{formatCurrency(insight.comparison.prior_cost)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-blue-600">{insight.comparison.current_period}</div>
                      <div className="font-semibold text-blue-800">{formatCurrency(insight.comparison.current_cost)}</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Period Info - show time period measured for insights without comparison */}
              {insight.period_info && !insight.comparison && (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs font-medium text-slate-600 mb-1">Time Period</div>
                  <div className="font-medium text-sm text-slate-800">{insight.period_info.label}</div>
                  {insight.period_info.description && (
                    <div className="text-xs text-slate-500 mt-1">{insight.period_info.description}</div>
                  )}
                  {insight.period_info.recent_avg !== undefined && insight.period_info.older_avg !== undefined && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <div className="text-xs text-slate-500">Earlier Avg</div>
                        <div className="font-medium text-sm">{formatCurrency(insight.period_info.older_avg)}/mo</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Recent Avg</div>
                        <div className="font-medium text-sm">{formatCurrency(insight.period_info.recent_avg)}/mo</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Metrics */}
              {(insight.location || insight.department || insight.metric_value) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {insight.location && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-muted-foreground">Location</div>
                      <div className="font-medium text-sm">{insight.location}</div>
                    </div>
                  )}
                  {insight.department && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-muted-foreground">Department</div>
                      <div className="font-medium text-sm">{insight.department}</div>
                    </div>
                  )}
                  {insight.metric_value !== undefined && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-xs text-muted-foreground">Metric</div>
                      <div className="font-medium text-sm">
                        {insight.metric_value.toLocaleString()}{insight.metric_unit}
                      </div>
                    </div>
                  )}
                  {insight.threshold_exceeded && (
                    <div className="bg-red-50 rounded-lg p-2">
                      <div className="text-xs text-red-600">Threshold</div>
                      <div className="font-medium text-sm text-red-700">
                        &gt;{insight.threshold_exceeded}{insight.metric_unit}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Breakdown if available */}
              {insight.breakdown && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">Breakdown</div>
                  <div className="space-y-1">
                    {insight.breakdown.map((item, idx) => (
                      <div key={item.department || `breakdown-${idx}`} className="flex justify-between text-sm bg-slate-50 rounded px-2 py-1">
                        <span>{item.department}</span>
                        <span className="font-medium">{formatCurrency(item.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                {(() => {
                  const empMatch = (insight.id || '').match(/employee[_\-](?:ot[_\-])?(\d+)/i);
                  return empMatch ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewEmployee && onViewEmployee(empMatch[1]);
                      }}
                      className="flex items-center gap-1 text-[#2E5C8A] border-[#2E5C8A]/30 hover:bg-[#2E5C8A]/5"
                      data-testid={`insight-view-employee-${empMatch[1]}`}
                    >
                      <User size={14} />
                      View Employee
                    </Button>
                  ) : null;
                })()}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDashboard(insight);
                  }}
                  className="flex items-center gap-1"
                  data-testid={`insight-view-dashboard-${insight.id}`}
                >
                  <ExternalLink size={14} />
                  View in Dashboard
                </Button>
                {insight.recommendation_type && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewRecommendation(insight);
                    }}
                    className="flex items-center gap-1"
                    data-testid={`insight-view-recommendation-${insight.id}`}
                  >
                    <ArrowRight size={14} />
                    See Recommendation
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ThresholdConfigPanel = ({ thresholds, definitions, onUpdate, onClose }) => {
  const [localThresholds, setLocalThresholds] = useState(thresholds);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    setLocalThresholds(prev => ({ ...prev, [id]: numValue }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(localThresholds);
    setHasChanges(false);
  };

  const handleReset = () => {
    const defaults = {};
    definitions.forEach(def => {
      defaults[def.id] = def.default;
    });
    setLocalThresholds(defaults);
    setHasChanges(true);
  };

  // Group definitions by category
  const groupedDefinitions = definitions.reduce((acc, def) => {
    const cat = def.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(def);
    return acc;
  }, {});

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-xl border border-slate-200 shadow-lg p-5 mb-6"
      data-testid="threshold-config-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg text-[#1e3a5f]">Alert Thresholds</h3>
          <p className="text-sm text-muted-foreground">Configure when insights are triggered</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={18} />
        </Button>
      </div>

      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
        {Object.entries(groupedDefinitions).map(([category, defs]) => (
          <div key={category}>
            <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
              {category.replace('_', ' ')}
            </h4>
            <div className="space-y-3">
              {defs.map(def => (
                <div key={def.id} className="flex items-center justify-between gap-4 bg-slate-50 rounded-lg p-3">
                  <div className="flex-1">
                    <Label className="text-sm font-medium">{def.name}</Label>
                    <p className="text-xs text-muted-foreground">{def.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={localThresholds[def.id] || def.default}
                      onChange={(e) => handleChange(def.id, e.target.value)}
                      className="w-24 text-right"
                      data-testid={`threshold-input-${def.id}`}
                    />
                    <span className="text-sm text-muted-foreground w-12">{def.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mt-4 pt-4 border-t">
        <Button variant="outline" size="sm" onClick={handleReset}>
          Reset to Defaults
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={!hasChanges}
            data-testid="save-thresholds-btn"
          >
            <Check size={14} className="mr-1" />
            Save Thresholds
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const Insights = () => {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [sortBy, setSortBy] = useState('all');
  const [showThresholdConfig, setShowThresholdConfig] = useState(false);
  const [thresholds, setThresholds] = useState({});
  const [thresholdDefinitions, setThresholdDefinitions] = useState([]);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [isStale, setIsStale] = useState(false);
  
  // Employee drill-down state
  const [employeeDrawerOpen, setEmployeeDrawerOpen] = useState(false);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState({ type: null, value: null });
  
  // Category definitions for sort tabs
  const categoryConfig = {
    all: { label: 'All Insights', icon: LayoutGrid, color: 'slate' },
    critical_alert: { label: 'Critical Alerts', icon: AlertTriangle, color: 'red' },
    trending: { label: 'Trending', icon: TrendingUp, color: 'orange' },
    cost_driver: { label: 'Cost Drivers', icon: DollarSign, color: 'purple' },
    quick_win: { label: 'Quick Wins', icon: Zap, color: 'green' },
    benchmark: { label: 'Benchmarks', icon: BarChart2, color: 'blue' }
  };

  // DB-freshness-aware cached fetch
  const fetchInsightsFn = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/insights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start_date: filters.start_date,
        end_date: filters.end_date,
        location: filters.location,
        department: filters.department,
        pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null,
        sort_by: 'dollar'
      })
    });
    if (!response.ok) throw new Error('Failed to fetch insights');
    return response.json();
  }, [filters.start_date, filters.end_date, filters.location, filters.department, filters.pay_types]);

  const { data: cachedInsightsData, loading: cacheLoading } = useCachedData(
    'insights_page',
    fetchInsightsFn,
    [filters.start_date, filters.end_date, filters.location, filters.department, JSON.stringify(filters.pay_types)]
  );

  useEffect(() => {
    if (cachedInsightsData) {
      setInsights(cachedInsightsData.insights || []);
      setSummary(cachedInsightsData.summary || null);
      setLastGenerated(new Date());
      setIsStale(false);
    }
  }, [cachedInsightsData]);

  const loading = cacheLoading || manualLoading;

  // Manual force-refresh
  const fetchInsights = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) return;
    setManualLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: filters.start_date,
          end_date: filters.end_date,
          location: filters.location,
          department: filters.department,
          pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null,
          sort_by: 'dollar'
        })
      });
      if (!response.ok) throw new Error('Failed to fetch insights');
      const data = await response.json();
      setInsights(data.insights || []);
      setSummary(data.summary);
      setLastGenerated(new Date());
      setIsStale(false);
      toast.success(`Generated ${data.summary?.total_insights || 0} insights`);
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setManualLoading(false);
    }
  }, [filters.start_date, filters.end_date, filters.location, filters.department, filters.pay_types]);

  const fetchThresholds = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/insights/thresholds`);
      if (!response.ok) throw new Error('Failed to fetch thresholds');
      const data = await response.json();
      setThresholds(data.values || {});
      setThresholdDefinitions(data.definitions || []);
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  }, []);

  const updateThresholds = async (newThresholds) => {
    try {
      const response = await fetch(`${API_URL}/api/insights/thresholds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thresholds: newThresholds })
      });
      if (!response.ok) throw new Error('Failed to update thresholds');
      setThresholds(newThresholds);
      toast.success('Thresholds updated');
      fetchInsights(true);
    } catch (error) {
      toast.error('Failed to update thresholds');
    }
  };

  // Fetch thresholds on mount
  useEffect(() => {
    fetchThresholds();
  }, [fetchThresholds]);

  const handleViewDashboard = (insight) => {
    // Check if this is an employee-specific insight
    const insightId = insight.id || '';
    const isEmployeeInsight = insightId.includes('employee_') || 
                              insight.title?.toLowerCase().includes('exceeded') ||
                              insight.category === 'cost_driver' && insightId.includes('employee');
    
    // Extract employee number if present in insight ID (e.g., "critical_employee_ot_12345")
    const employeeMatch = insightId.match(/employee[_\-](?:ot[_\-])?(\d+)/i);
    
    if (isEmployeeInsight && employeeMatch) {
      // Open employee drawer instead of navigating
      setSelectedEmployeeFilter({
        type: 'employee_number',
        value: employeeMatch[1],
        additionalFilters: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          location: insight.location || filters.location,
          department: insight.department || filters.department
        }
      });
      setEmployeeDrawerOpen(true);
    } else if (insight.location || insight.department) {
      // For location/department insights, open employee drawer with those filters
      setSelectedEmployeeFilter({
        type: insight.department ? 'cost_center' : 'location',
        value: insight.department || insight.location,
        additionalFilters: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          location: insight.location || filters.location,
          department: insight.department || filters.department
        }
      });
      setEmployeeDrawerOpen(true);
    } else {
      // Navigate to dashboard for general insights
      const params = new URLSearchParams();
      if (insight.location) params.set('location', insight.location);
      if (insight.department) params.set('department', insight.department);
      navigate(`/dashboard?${params.toString()}`);
    }
  };

  const handleViewRecommendation = (insight) => {
    // Navigate to recommendations filtered by type
    const params = new URLSearchParams();
    if (insight.recommendation_type) params.set('type', insight.recommendation_type);
    if (insight.location) params.set('location', insight.location);
    navigate(`/recommendations?${params.toString()}`);
  };

  const handleViewEmployee = (employeeId) => {
    navigate(`/employee/${employeeId}`);
  };

  // Filter and group insights by category based on selected filter
  const filteredInsights = useMemo(() => {
    if (sortBy === 'all') {
      return insights;
    }
    return insights.filter(insight => insight.category === sortBy);
  }, [insights, sortBy]);

  // Group insights by category for display
  const groupedInsights = useMemo(() => {
    const groups = {};
    filteredInsights.forEach(insight => {
      const cat = insight.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(insight);
    });
    return groups;
  }, [filteredInsights]);

  // Category order for display
  const categoryOrder = ['critical_alert', 'trending', 'cost_driver', 'quick_win', 'benchmark', 'prediction'];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="insights-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Lightbulb className="text-amber-500" size={28} />
            <h1 className="text-3xl font-bold text-[#1e3a5f]">Insights</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            AI-detected issues requiring attention
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowThresholdConfig(!showThresholdConfig)}
            className="flex items-center gap-2"
            data-testid="toggle-thresholds-btn"
          >
            <Settings size={16} />
            Thresholds
          </Button>
          <Button
            onClick={fetchInsights}
            disabled={loading}
            className="flex items-center gap-2"
            data-testid="refresh-insights-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Analyzing...' : 'Refresh Insights'}
          </Button>
        </div>
      </div>

      {/* Threshold Configuration Panel */}
      <AnimatePresence>
        {showThresholdConfig && (
          <ThresholdConfigPanel
            thresholds={thresholds}
            definitions={thresholdDefinitions}
            onUpdate={updateThresholds}
            onClose={() => setShowThresholdConfig(false)}
          />
        )}
      </AnimatePresence>

      {/* Summary Stats - Show skeleton on initial load */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[...Array(5)].map((_, i) => (
            <div key={`ins-skel-${i}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
      ) : summary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6"
        >
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-total">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Lightbulb size={16} />
              Total Insights
            </div>
            <div className="text-2xl font-bold text-[#1e3a5f]">{summary.total_insights}</div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-dollar">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <DollarSign size={16} />
              Total Impact
            </div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_dollar_impact)}</div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-critical">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <AlertTriangle size={16} className="text-red-500" />
              Critical
            </div>
            <div className="text-2xl font-bold text-red-600">{summary.critical_count}</div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-urgent">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Clock size={16} className="text-orange-500" />
              High Urgency
            </div>
            <div className="text-2xl font-bold text-orange-600">{summary.high_urgency_count}</div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-easy">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Zap size={16} className="text-green-500" />
              Easy Wins
            </div>
            <div className="text-2xl font-bold text-green-600">{summary.easy_wins_count}</div>
          </div>
        </motion.div>
      )}

      {/* Category Filter Tabs */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground mr-2">Filter:</span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(categoryConfig).map(([key, config]) => {
                const Icon = config.icon;
                const count = key === 'all' ? insights.length : insights.filter(i => i.category === key).length;
                const isActive = sortBy === key;
                
                // Color mapping for each category
                const colorClasses = {
                  slate: isActive ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                  red: isActive ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100',
                  orange: isActive ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100',
                  purple: isActive ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100',
                  green: isActive ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100',
                  blue: isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
                  cyan: isActive ? 'bg-cyan-600 text-white' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                };
                
                return (
                  <button
                    key={key}
                    onClick={() => setSortBy(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${colorClasses[config.color]}`}
                    data-testid={`filter-${key}`}
                  >
                    <Icon size={14} />
                    <span>{config.label}</span>
                    <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-black/10'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {lastGenerated && (
            <div className="text-xs text-muted-foreground">
              Last updated: {lastGenerated.toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Insights by Category */}
      {/* Insights Content - Show skeleton on initial load */}
      {loading ? (
        <div className="space-y-8">
          {/* Skeleton for categories */}
          {[1, 2, 3].map((n, catIdx) => (
            <div key={`cat-skel-${n}`}>
              <div className="flex items-center gap-3 mb-4">
                <Skeleton className="w-8 h-8 rounded-lg" />
                <div>
                  <Skeleton className="h-5 w-32 mb-1" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map((n) => (
                  <div key={`insight-skel-${n}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start gap-4">
                      <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex gap-2 mb-2">
                          <Skeleton className="h-5 w-20" />
                          <Skeleton className="h-5 w-16" />
                        </div>
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="animate-spin text-muted-foreground" size={32} />
          <span className="ml-3 text-muted-foreground">Refreshing insights...</span>
        </div>
      ) : filteredInsights.length === 0 ? (
        <div className="text-center py-20 bg-slate-50 rounded-xl">
          <Lightbulb className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-500">
            {sortBy === 'all' ? 'No Insights Found' : `No ${categoryConfig[sortBy]?.label || 'Insights'} Found`}
          </h3>
          <p className="text-muted-foreground mt-1">
            {sortBy === 'all' 
              ? 'All metrics are within normal thresholds. Great job!'
              : `Try selecting "All Insights" to see other categories.`
            }
          </p>
          {sortBy !== 'all' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortBy('all')}
              className="mt-4"
            >
              Show All Insights
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {categoryOrder.map(category => {
            const categoryInsights = groupedInsights[category];
            if (!categoryInsights || categoryInsights.length === 0) return null;
            
            const config = CATEGORY_CONFIG[category];
            const CategoryIcon = config.icon;
            
            return (
              <div key={category} data-testid={`insight-category-${category}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 ${config.color} rounded-lg flex items-center justify-center`}>
                    <CategoryIcon className="text-white" size={18} />
                  </div>
                  <div>
                    <h2 className="font-semibold text-[#1e3a5f]">{config.label}</h2>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                  <Badge variant="secondary" className="ml-auto">
                    {categoryInsights.length}
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  {categoryInsights.map(insight => (
                    <InsightCard
                      key={insight.id}
                      insight={insight}
                      onViewDashboard={handleViewDashboard}
                      onViewRecommendation={handleViewRecommendation}
                      onViewEmployee={handleViewEmployee}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Employee Drill-Down Drawer */}
      <EmployeeDrillDown
        isOpen={employeeDrawerOpen}
        onClose={() => setEmployeeDrawerOpen(false)}
        filterType={selectedEmployeeFilter.type}
        filterValue={selectedEmployeeFilter.value}
        additionalFilters={selectedEmployeeFilter.additionalFilters}
      />
    </div>
  );
};

export default Insights;
